import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import InventoryStyleListTable from '../../../accounting/inventory/components/InventoryStyleListTable';
import type { DataTableColumn } from '../../../accounting/shared/AccountingDataTable';
import FixedOrderFormDialog from '../../dialogs/FixedOrderFormDialog';
import FixedProduceDialog from '../../dialogs/FixedProduceDialog';
import type { FixedOrderListState } from '../../hooks/useFixedOrderList';
import { ProductionApiError, deleteFixedOrder } from '../../services/productionService';
import type { ProductionFixedOrder, ProductionOrder } from '../../types/production.types';
import { formatQuantity } from '../../utils/productionFormat';

const PAGE_SIZE = 25;

interface FixedOrdersSectionProps {
  list: FixedOrderListState;
  /** Üret düğmesi: "Sabit üretim yapabilir" ya da taslak + tamamlama. */
  canProduce: boolean;
  /** Düzenle / sil: taslak oluşturma yetkisi. */
  canManage: boolean;
  /** Şube sütunu ve filtresi (şirkette şube varsa). */
  hasBranches: boolean;
  /** Üretim yapılınca fiş listesi yenilensin. */
  onProduced: () => void;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "Bugün 10:30 · 2 kez · URT-…"; hiç üretilmediyse "—". */
export function formatLastProduced(t: TFunction, row: ProductionFixedOrder, locale: string, now = new Date()): string {
  if (!row.lastProducedAt) return '—';
  const at = new Date(row.lastProducedAt);
  if (Number.isNaN(at.getTime())) return '—';
  const time = at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  let when: string;
  if (sameDay(at, now)) when = t('fixedOrders.lastProducedToday', { time });
  else if (sameDay(at, yesterday)) when = t('fixedOrders.lastProducedYesterday', { time });
  else when = `${at.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${time}`;
  const parts = [when];
  if (row.lastProducedTimes) parts.push(t('fixedOrders.lastProducedTimes', { count: row.lastProducedTimes, times: formatQuantity(row.lastProducedTimes) }));
  if (row.lastOrderNumber) parts.push(row.lastOrderNumber);
  return parts.join(' · ');
}

/** 1250 → "1,3 B" gibi kısa sayı. */
function shortCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/**
 * Üretim Fişleri sayfasının "Sabit Üretimler" sekmesi.
 * Tanım Basit Üretim Reçeteleri listesinin ⋮ menüsünden yapılır; burada aranır, üretilir, düzenlenir, silinir.
 */
const FixedOrdersSection: React.FC<FixedOrdersSectionProps> = ({ list, canProduce, canManage, hasBranches, onProduced }) => {
  const { t, i18n } = useTranslation('production');
  const locale = i18n.language === 'en' ? 'en-US' : 'tr-TR';
  const { rows, loading, error, setError, reload } = list;
  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<ProductionFixedOrder | null>(null);
  const [producing, setProducing] = useState<ProductionFixedOrder | null>(null);
  const [deleting, setDeleting] = useState<ProductionFixedOrder | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: ProductionFixedOrder } | null>(null);

  const branches = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((row) => {
      if (row.branchId) map.set(row.branchId, row.branchName || row.branchId);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], locale));
  }, [rows, locale]);

  const warehouses = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((row) => {
      map.set(row.inputWarehouseId, row.inputWarehouseName || '—');
      map.set(row.outputWarehouseId, row.outputWarehouseName || '—');
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], locale));
  }, [rows, locale]);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase(locale);
    return rows.filter((row) => {
      if (branchId && row.branchId !== branchId) return false;
      if (warehouseId && row.inputWarehouseId !== warehouseId && row.outputWarehouseId !== warehouseId) return false;
      if (!term) return true;
      return [row.productName, row.name, row.stockCode]
        .some((text) => (text || '').toLocaleLowerCase(locale).includes(term));
    });
  }, [rows, search, branchId, warehouseId, locale]);

  const filtersDirty = Boolean(search.trim() || branchId || warehouseId);

  // Filtre değişince ilk sayfaya dön; satır silinince sayfa boş kalmasın.
  useEffect(() => { setPage(0); }, [search, branchId, warehouseId]);
  useEffect(() => {
    const last = Math.max(0, Math.ceil(filtered.length / PAGE_SIZE) - 1);
    if (page > last) setPage(last);
  }, [filtered.length, page]);

  const pageRows = useMemo(() => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [filtered, page]);

  const handleProduced = (order: ProductionOrder) => {
    setSuccess(order.outputRangeWarning
      ? t('fixedOrders.produce.successWarning', { number: order.orderNumber, warning: order.outputRangeWarning })
      : t('fixedOrders.produce.success', { number: order.orderNumber }));
    onProduced();
    void reload();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteFixedOrder(deleting.id);
      setDeleting(null);
      await reload();
    } catch (err) {
      setError(err instanceof ProductionApiError ? err.message : t('fixedOrders.deleteFailed'));
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const columns = useMemo<DataTableColumn<ProductionFixedOrder>[]>(() => {
    const cols: DataTableColumn<ProductionFixedOrder>[] = [
      {
        id: 'product',
        label: t('fixedOrders.columns.product'),
        minWidth: '180px',
        render: (row) => {
          const name = row.name?.trim();
          return (
            <Box sx={{ maxWidth: 260 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={row.productName || ''} placement="top-start">
                  <Typography variant="body2" fontWeight={600} sx={ellipsis}>{row.productName || '—'}</Typography>
                </Tooltip>
                {!row.isActive && <Chip size="small" label={t('fixedOrders.inactive')} />}
                {!row.hasActiveRecipe && <Chip size="small" color="error" variant="outlined" label={t('fixedOrders.noRecipeBadge')} />}
              </Box>
              {name && name !== row.productName ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={ellipsis}>{row.name}</Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        id: 'quantity',
        label: t('fixedOrders.columns.quantity'),
        align: 'right',
        minWidth: '100px',
        render: (row) => formatQuantity(row.quantity, row.unit),
      },
      {
        id: 'warehouses',
        label: t('fixedOrders.colWarehouses'),
        minWidth: '150px',
        render: (row) => {
          const input = row.inputWarehouseName || '—';
          const output = row.outputWarehouseName || '—';
          const text = row.inputWarehouseId === row.outputWarehouseId ? input : `${input} → ${output}`;
          return (
            <Tooltip title={text}>
              <Typography variant="body2" sx={{ ...ellipsis, maxWidth: 220, fontSize: 'inherit' }}>{text}</Typography>
            </Tooltip>
          );
        },
      },
      {
        id: 'branch',
        label: t('fixedOrders.columns.branch'),
        minWidth: '100px',
        render: (row) => (
          <Typography variant="body2" sx={{ ...ellipsis, maxWidth: 150, fontSize: 'inherit' }}>{row.branchName?.trim() || '—'}</Typography>
        ),
      },
      {
        id: 'lastProduced',
        label: t('fixedOrders.colLastProduced'),
        minWidth: '170px',
        render: (row) => {
          const text = formatLastProduced(t, row, locale);
          if (!row.lastProducedAt) return text;
          return (
            <Tooltip title={row.lastProducedBy ? t('fixedOrders.lastProducedBy', { user: row.lastProducedBy }) : ''}>
              <span>{text}</span>
            </Tooltip>
          );
        },
      },
      {
        id: 'produceCount',
        label: t('fixedOrders.colProduceCount'),
        align: 'right',
        minWidth: '90px',
        render: (row) => (
          <Tooltip title={t('fixedOrders.produceCountTooltip', { count: row.produceCount ?? 0, times: formatQuantity(row.produceCount ?? 0) })}>
            <span>{shortCount(row.produceCount ?? 0, locale)}</span>
          </Tooltip>
        ),
      },
      {
        id: 'actions',
        label: t('fixedOrders.columns.actions'),
        align: 'right',
        minWidth: '130px',
        render: (row) => {
          const produceDisabled = !row.isActive || !row.hasActiveRecipe;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              {canProduce && (
                <Tooltip title={produceDisabled ? (row.hasActiveRecipe ? t('fixedOrders.inactiveCannotProduce') : t('fixedOrders.noActiveRecipe')) : ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      disabled={produceDisabled}
                      onClick={(event) => { event.stopPropagation(); setProducing(row); }}
                      sx={{ minWidth: 92, fontWeight: 700 }}
                    >
                      {t('fixedOrders.produceButton')}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {canManage && (
                <IconButton
                  size="small"
                  aria-label={t('fixedOrders.rowMenu')}
                  onClick={(event) => { event.stopPropagation(); setMenu({ anchor: event.currentTarget, row }); }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        },
      },
    ];
    return hasBranches ? cols : cols.filter((col) => col.id !== 'branch');
  }, [t, locale, canProduce, canManage, hasBranches]);

  const emptyMessage = filtersDirty ? t('fixedOrders.emptyFiltered') : t('fixedOrders.emptyHint');

  return (
    <Box>
      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('fixedOrders.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {hasBranches && (
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('fixedOrders.branch')}</InputLabel>
              <Select value={branchId} label={t('fixedOrders.branch')} onChange={(event) => setBranchId(event.target.value)}>
                <MenuItem value="">{t('fixedOrders.allBranches')}</MenuItem>
                {branches.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('fixedOrders.warehouseFilter')}</InputLabel>
            <Select value={warehouseId} label={t('fixedOrders.warehouseFilter')} onChange={(event) => setWarehouseId(event.target.value)}>
              <MenuItem value="">{t('fixedOrders.allWarehouses')}</MenuItem>
              {warehouses.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        {filtersDirty && (
          <Grid item xs={12} md="auto">
            <Button onClick={() => { setSearch(''); setBranchId(''); setWarehouseId(''); }}>
              {t('orders.clearFilters')}
            </Button>
          </Grid>
        )}
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <InventoryStyleListTable
        columns={columns}
        rows={pageRows}
        getRowId={(row) => row.id}
        loading={loading}
        totalCount={filtered.length}
        page={page}
        rowsPerPage={PAGE_SIZE}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={() => undefined}
        rowsPerPageOptions={[PAGE_SIZE]}
        emptyMessage={loading ? '' : emptyMessage}
        ariaLabel={t('fixedOrders.sectionTitle')}
        columnResizeStorageKey="production-fixed-orders"
        toolbarInfo={t('fixedOrders.recordCount', { count: filtered.length })}
      />

      <Menu anchorEl={menu?.anchor ?? null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setEditing(menu?.row ?? null); setMenu(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('fixedOrders.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setDeleting(menu?.row ?? null); setMenu(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{t('fixedOrders.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <FixedOrderFormDialog
        open={Boolean(editing)}
        initial={editing}
        onClose={() => setEditing(null)}
        onSaved={() => void reload()}
      />
      <FixedProduceDialog
        open={Boolean(producing)}
        fixedOrder={producing}
        onClose={() => setProducing(null)}
        onProduced={handleProduced}
      />
      <Dialog open={Boolean(deleting)} onClose={deleteBusy ? undefined : () => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('fixedOrders.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t('fixedOrders.deleteConfirm', { name: deleting?.name || deleting?.productName || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>{t('fixedOrders.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteBusy}>
            {deleteBusy ? <CircularProgress size={18} color="inherit" /> : t('fixedOrders.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)} variant="filled">{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FixedOrdersSection;
